import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAuth } from "@/context/AuthContext";
import { getUserCompanies } from "@/api/user.service";
import {
  CATEGORIA_TODAS,
  listContribuicoes,
  listEventos,
  localizarCategorias,
  type Contribuicao,
  type Evento,
} from "@/api/metas.service";
import { partesDe, rotuloMesChave } from "./metasFormato";

/** Parâmetros do cenário que a listagem já carregou — o que o cálculo consumiu. */
export interface ParametrosCenario {
  id: string;
  descricao: string;
  mesMeta: string;
  /** Peso de cada mês de referência, indexado por "AAAA-MM". */
  impactos: Record<string, string>;
  crescimento: string;
  propagarPai: boolean;
}

interface Props {
  cenario: ParametrosCenario | null;
  clientId: string;
  onClose: () => void;
}

function formatarData(iso?: string) {
  if (!iso) return "";
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

function formatarCrescimento(valor: string) {
  const numero = Number(String(valor).replace(",", "."));
  if (!Number.isFinite(numero)) return "0%";
  return `${numero > 0 ? "+" : ""}${numero.toLocaleString("pt-BR")}%`;
}

/**
 * O evento entra no cálculo quando está ativo e seu período toca o mês da meta
 * — mesma regra de mapearEventosParaCenario (Product Runtime). Evento anual
 * vale para as mesmas datas em todo ano.
 */
function eventoNoMes(e: Evento, ano: number, mes: number) {
  if (!e.ativo || !e.dataInicial || !e.dataFinal) return false;
  const [, mi, di] = e.dataInicial.split("-").map(Number);
  const [, mf, df] = e.dataFinal.split("-").map(Number);
  const inicio = e.repetirAnualmente ? new Date(ano, mi! - 1, di) : new Date(`${e.dataInicial}T00:00:00`);
  const fim = e.repetirAnualmente ? new Date(ano, mf! - 1, df) : new Date(`${e.dataFinal}T00:00:00`);
  const primeiro = new Date(ano, mes - 1, 1);
  const ultimo = new Date(ano, mes, 0);
  return fim >= primeiro && inicio <= ultimo;
}

function formatarPeriodo(e: Evento) {
  const inicio = formatarData(e.dataInicial).slice(0, e.repetirAnualmente ? 5 : 10);
  const fim = formatarData(e.dataFinal).slice(0, e.repetirAnualmente ? 5 : 10);
  return inicio === fim ? inicio : `${inicio} a ${fim}`;
}

export function MetasParametrosModal({ cenario, clientId, onClose }: Props) {
  const { user } = useAuth();
  const userId = user!.id;
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [contribuicoes, setContribuicoes] = useState<Contribuicao[]>([]);
  const [lojas, setLojas] = useState<Record<string, string>>({});
  const [categorias, setCategorias] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const id = cenario?.id;
  const anoMeta = cenario ? partesDe(cenario.mesMeta).ano : 0;
  const mesMeta = cenario ? partesDe(cenario.mesMeta).mes : 0;
  useEffect(() => {
    if (!id) return;
    let cancelado = false;
    setLoading(true);
    setEventos([]);
    setContribuicoes([]);
    setCategorias({});

    (async () => {
      try {
        // Lojas e eventos não derrubam o modal se falharem: os códigos aparecem no lugar dos nomes.
        const [todosEventos, todasContribuicoes, empresas] = await Promise.all([
          listEventos(clientId).catch(() => [] as Evento[]),
          listContribuicoes(clientId).catch(() => [] as Contribuicao[]),
          getUserCompanies(userId).catch(() => []),
        ]);
        if (cancelado) return;

        const mapaLojas: Record<string, string> = {};
        for (const c of empresas) {
          if (c.client_id === clientId && c.erp_code) mapaLojas[String(c.erp_code)] = c.name;
        }
        const aplicaveis = (todosEventos ?? []).filter((e) => eventoNoMes(e, anoMeta, mesMeta));

        // Mesma regra do cálculo: contribuição ativa cujo mês de referência é o mês da meta.
        const contribuicoesDoMes = (todasContribuicoes ?? []).filter(
          (c) => c.ativo && c.anoReferencia === anoMeta && c.mesReferencia === mesMeta,
        );

        setEventos(aplicaveis);
        setContribuicoes(contribuicoesDoMes);
        setLojas(mapaLojas);

        const codigos = new Set<string>();
        aplicaveis.forEach((e) =>
          e.regras.forEach((r) => r.categoriaId !== CATEGORIA_TODAS && codigos.add(r.categoriaId)),
        );
        contribuicoesDoMes.forEach((c) =>
          c.regras.forEach((r) => r.categoriaId !== CATEGORIA_TODAS && codigos.add(r.categoriaId)),
        );
        if (codigos.size) {
          const achadas = await localizarCategorias(clientId, [...codigos]).catch(() => []);
          if (!cancelado) setCategorias(Object.fromEntries(achadas.map((c) => [c.id, c.nome])));
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [id, clientId, userId, anoMeta, mesMeta]);

  const nomeLoja = (cod: string | number) => lojas[String(cod)] ?? `Loja ${cod}`;

  const periodos = Object.entries(cenario?.impactos ?? {}).sort(([a], [b]) => a.localeCompare(b));
  const totalPeso = periodos.reduce((acc, [, peso]) => acc + (Number(String(peso).replace(",", ".")) || 0), 0);

  return (
    <Dialog open={!!cenario} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>Parâmetros do cálculo</DialogTitle>
      <DialogContent dividers>
        {cenario && (
          <Stack gap={3}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {cenario.descricao}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Meta de {rotuloMesChave(cenario.mesMeta)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                Períodos históricos selecionados
              </Typography>
              <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>MÊS DE REFERÊNCIA</TableCell>
                      <TableCell align="right">PESO</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {periodos.map(([mes, peso]) => (
                      <TableRow key={mes}>
                        <TableCell>{rotuloMesChave(mes)}</TableCell>
                        <TableCell align="right">
                          {(Number(String(peso).replace(",", ".")) || 0).toLocaleString("pt-BR", {
                            minimumFractionDigits: 3,
                            maximumFractionDigits: 3,
                          })}
                          %
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {totalPeso.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} gap={4}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Índice de crescimento aplicado
                </Typography>
                <Typography variant="h6">{formatarCrescimento(cenario.crescimento)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Direção do cálculo
                </Typography>
                <Typography variant="h6">{cenario.propagarPai ? "Ascendente" : "Descendente"}</Typography>
              </Box>
            </Stack>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                Feriados e sazonalidades considerados
              </Typography>
              {loading ? null : eventos.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum feriado ou sazonalidade ativo em {rotuloMesChave(cenario.mesMeta)}.
                </Typography>
              ) : (
                <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>EVENTO</TableCell>
                        <TableCell>PERÍODO</TableCell>
                        <TableCell>LOJAS</TableCell>
                        <TableCell>CATEGORIAS E EFEITO</TableCell>
                        <TableCell>MESES DE REFERÊNCIA</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eventos.map((e, i) => (
                        <TableRow key={e.id ?? i} sx={{ verticalAlign: "top" }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {e.nome}
                            </Typography>
                            <Chip size="small" variant="outlined" label={e.tipo} />
                          </TableCell>
                          <TableCell>
                            {formatarPeriodo(e)}
                            {e.repetirAnualmente ? " (anual)" : ""}
                          </TableCell>
                          <TableCell>
                            {e.abrangencia === "global" || e.lojas.length === 0
                              ? "Todas as lojas"
                              : e.lojas.map(nomeLoja).join(", ")}
                          </TableCell>
                          <TableCell>
                            {e.regras.map((r) => (
                              <Typography key={r.categoriaId} variant="body2">
                                {r.categoriaId === CATEGORIA_TODAS
                                  ? "Todas as categorias"
                                  : (categorias[r.categoriaId] ?? r.categoriaId)}
                                {": "}
                                {r.efeito === "referencia"
                                  ? "somente referência"
                                  : `${r.percentual > 0 ? "+" : ""}${r.percentual.toLocaleString("pt-BR")}%`}
                              </Typography>
                            ))}
                          </TableCell>
                          <TableCell>
                            {e.periodos.length
                              ? e.periodos
                                  .map(
                                    (p) =>
                                      `${rotuloMesChave(`${p.ano}-${String(p.mes).padStart(2, "0")}`)} (${p.percentual.toLocaleString("pt-BR")}%)`,
                                  )
                                  .join(", ")
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                Contribuições consideradas
              </Typography>
              {loading ? null : contribuicoes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma contribuição ativa para {rotuloMesChave(cenario.mesMeta)}.
                </Typography>
              ) : (
                <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>CONTRIBUIÇÃO</TableCell>
                        <TableCell>CATEGORIA</TableCell>
                        <TableCell align="right">PERCENTUAL</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contribuicoes.flatMap((c, i) =>
                        c.regras.map((r, j) => (
                          <TableRow key={`${c.id ?? i}-${r.categoriaId}-${j}`}>
                            <TableCell>{j === 0 ? c.nome : ""}</TableCell>
                            <TableCell>
                              {r.categoriaId === CATEGORIA_TODAS
                                ? "Todas as categorias"
                                : (categorias[r.categoriaId] ?? r.categoriaId)}
                            </TableCell>
                            <TableCell align="right">
                              {r.percentual.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}%
                            </TableCell>
                          </TableRow>
                        )),
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
